import { fetchPack } from "../api/catalog";
import { offlineSupported, packs } from "./offlineStore";

export const AUTO_UPDATE_KEY = "prepai.offline.autoUpdate";

/** How long a downloaded pack is trusted before an automatic refresh. */
const STALE_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

interface NetworkInformationLike {
  type?: string;
  saveData?: boolean;
}

/**
 * True only when the browser says the connection is Wi-Fi (or wired).
 *
 * `navigator.connection.type` exists on Chrome for Android, which is most of
 * our students. Where it is missing — iOS Safari, desktop Firefox — we cannot
 * tell Wi-Fi from a metered bundle, so the answer is no: a background download
 * on someone's data is exactly what this setting promises not to do.
 */
export function onUnmeteredConnection(
  connection: NetworkInformationLike | undefined = (
    navigator as Navigator & { connection?: NetworkInformationLike }
  ).connection,
): boolean {
  if (!connection || connection.saveData) return false;
  return connection.type === "wifi" || connection.type === "ethernet";
}

export function autoUpdateEnabled(): boolean {
  try {
    return localStorage.getItem(AUTO_UPDATE_KEY) !== "false";
  } catch {
    return true;
  }
}

/**
 * Refresh downloaded subjects that are more than a week old — on Wi-Fi only,
 * and only when the student left "Keep my subjects updated on Wi-Fi" on.
 *
 * Only subjects already on the phone are refreshed; nothing new is downloaded
 * without the student asking. Returns how many packs were refreshed.
 */
export async function refreshPacksOnWifi(now: number = Date.now()): Promise<number> {
  if (!offlineSupported() || !autoUpdateEnabled() || !onUnmeteredConnection()) return 0;

  let refreshed = 0;
  // Every exam's packs, each refreshed for its own exam: a WAEC pack must not
  // come back as JAMB questions because the app happens to be showing JAMB.
  for (const pack of await packs.everything()) {
    if (now - pack.downloadedAt < STALE_AFTER_MS) continue;
    try {
      const fresh = await fetchPack(pack.subjectId, pack.exam ?? null);
      await packs.save({
        ...pack,
        questions: fresh.questions,
        downloadedAt: now,
        bytes: new Blob([JSON.stringify(fresh.questions)]).size,
      });
      refreshed += 1;
    } catch {
      // Lost the connection part-way: the old pack still works; try next open.
    }
  }
  return refreshed;
}
